import React, { useState, useEffect } from 'react';
import { Goal, LifeCategory } from './Goals/types';

interface ImportTask {
  id: string;
  title: string;
  goalId: string;
  categoryId: string;
  status: 'planned' | 'in_progress_mine' | 'in_progress_waiting' | 'today' | 'done' | 'archived';
  archivedAt: string;
  selected: boolean;
}

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (tasks: any[]) => void;
  goals: Goal[];
  categories: LifeCategory[];
}

const BulkImportModal: React.FC<BulkImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
  goals,
  categories
}) => {
  const [importText, setImportText] = useState('');
  const [tasks, setTasks] = useState<ImportTask[]>([]);
  const [showTable, setShowTable] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  // Массовые значения для применения к выбранным
  const [bulkGoalId, setBulkGoalId] = useState('');
  const [bulkCategoryId, setBulkCategoryId] = useState('');
  const [bulkStatus, setBulkStatus] = useState<'planned' | 'in_progress_mine' | 'in_progress_waiting' | 'today' | 'done' | 'archived'>('planned');
  const [bulkArchivedAt, setBulkArchivedAt] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Сброс состояния при открытии
      setImportText('');
      setTasks([]);
      setShowTable(false);
      setSelectAll(false);
    }
  }, [isOpen]);

  // Парсинг вставленного текста
  const handleParseText = () => {
    const lines = importText.trim().split('\n');
    if (lines.length === 0) return;

    const parsedTasks: ImportTask[] = [];

    lines.forEach((line, index) => {
      const parts = line.split('\t'); // Табуляция из Excel
      
      if (parts.length >= 1 && parts[0].trim()) {
        // Формат: Название[TAB]Цель[TAB]Категория[TAB]Статус[TAB]Дата
        const title = parts[0].trim();
        const goalName = parts[1]?.trim() || '';
        const categoryName = parts[2]?.trim() || '';
        const status = (parts[3]?.trim() || 'planned') as 'planned' | 'in_progress_mine' | 'in_progress_waiting' | 'today' | 'done' | 'archived';
        const archivedAt = parts[4]?.trim() || '';

        // Поиск ID цели по названию
        const goal = goals.find(g => g.name.toLowerCase() === goalName.toLowerCase());
        const category = categories.find(c => c.name.toLowerCase() === categoryName.toLowerCase());

        parsedTasks.push({
          id: `temp-${index}`,
          title,
          goalId: goal?.id.toString() || '', // По умолчанию без цели
          categoryId: category?.id.toString() || (categories.length > 0 ? categories[0].id.toString() : ''),
          status,
          archivedAt,
          selected: false
        });
      }
    });

    setTasks(parsedTasks);
    setShowTable(true);
  };

  // Выбор всех/снятие всех
  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setTasks(tasks.map(t => ({ ...t, selected: newSelectAll })));
  };

  // Изменение чекбокса отдельной задачи
  const handleToggleTask = (id: string) => {
    setTasks(tasks.map(t => 
      t.id === id ? { ...t, selected: !t.selected } : t
    ));
  };

  // Изменение поля задачи
  const handleTaskFieldChange = (id: string, field: keyof ImportTask, value: any) => {
    setTasks(tasks.map(t => 
      t.id === id ? { ...t, [field]: value } : t
    ));
  };

  // Применить значение ко всем выбранным
  const applyToSelected = (field: keyof ImportTask, value: any) => {
    setTasks(tasks.map(t => 
      t.selected ? { ...t, [field]: value } : t
    ));
  };

  // Удаление выбранных
  const handleDeleteSelected = () => {
    setTasks(tasks.filter(t => !t.selected));
  };

  // Импорт задач
  const handleImportTasks = () => {
    const tasksToImport = tasks.map(t => ({
      title: t.title,
      goalId: t.goalId && t.goalId !== 'none' ? parseInt(t.goalId) : null,
      categoryId: t.categoryId ? parseInt(t.categoryId) : null,
      status: t.status,
      archivedAt: t.status === 'archived' ? (t.archivedAt || new Date().toISOString().split('T')[0]) : null
    }));

    onImport(tasksToImport);
    onClose();
  };

  if (!isOpen) return null;

  const selectedCount = tasks.filter(t => t.selected).length;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        width: '95%',
        maxWidth: '1400px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '25px 30px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
            📥 Массовый импорт задач
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0',
              lineHeight: '1'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '30px' }}>
          {!showTable ? (
            // Форма вставки текста
            <div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                  Вставьте список задач (каждая задача отдельной строкой):
                </label>
                <textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder="Купить продукты&#10;Сходить к врачу&#10;Оплатить счета&#10;Позвонить родителям"
                  style={{
                    width: '100%',
                    minHeight: '250px',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <button
                onClick={handleParseText}
                disabled={!importText.trim()}
                style={{
                  padding: '14px 32px',
                  background: importText.trim() ? '#667eea' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: importText.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Далее →
              </button>
            </div>
          ) : (
            // Таблица редактирования
            <div>
              {/* Массовые операции */}
              <div style={{
                background: '#f9fafb',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: 'bold', color: '#374151' }}>
                  Массовые операции {selectedCount > 0 && `(выбрано: ${selectedCount})`}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>
                      Цель:
                    </label>
                    <select
                      value={bulkGoalId}
                      onChange={(e) => setBulkGoalId(e.target.value)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                    >
                      <option value="">Выберите цель</option>
                      <option value="none">Без цели</option>
                      {goals.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => applyToSelected('goalId', bulkGoalId)}
                      disabled={!bulkGoalId || selectedCount === 0}
                      style={{
                        marginTop: '5px',
                        padding: '6px 12px',
                        background: bulkGoalId && selectedCount > 0 ? '#667eea' : '#e5e7eb',
                        color: bulkGoalId && selectedCount > 0 ? 'white' : '#9ca3af',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: bulkGoalId && selectedCount > 0 ? 'pointer' : 'not-allowed',
                        width: '100%'
                      }}
                    >
                      Применить
                    </button>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>
                      Категория:
                    </label>
                    <select
                      value={bulkCategoryId}
                      onChange={(e) => setBulkCategoryId(e.target.value)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                    >
                      <option value="">Выберите категорию</option>
                      {categories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => applyToSelected('categoryId', bulkCategoryId)}
                      disabled={!bulkCategoryId || selectedCount === 0}
                      style={{
                        marginTop: '5px',
                        padding: '6px 12px',
                        background: bulkCategoryId && selectedCount > 0 ? '#667eea' : '#e5e7eb',
                        color: bulkCategoryId && selectedCount > 0 ? 'white' : '#9ca3af',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: bulkCategoryId && selectedCount > 0 ? 'pointer' : 'not-allowed',
                        width: '100%'
                      }}
                    >
                      Применить
                    </button>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>
                      Статус:
                    </label>
                    <select
                      value={bulkStatus}
                      onChange={(e) => setBulkStatus(e.target.value as any)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                    >
                      <option value="planned">Запланировано</option>
                      <option value="in_progress_mine">В процессе (мое)</option>
                      <option value="in_progress_waiting">В процессе (жду)</option>
                      <option value="today">Сегодня</option>
                      <option value="done">Готово</option>
                      <option value="archived">Архив</option>
                    </select>
                    <button
                      onClick={() => applyToSelected('status', bulkStatus)}
                      disabled={selectedCount === 0}
                      style={{
                        marginTop: '5px',
                        padding: '6px 12px',
                        background: selectedCount > 0 ? '#667eea' : '#e5e7eb',
                        color: selectedCount > 0 ? 'white' : '#9ca3af',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
                        width: '100%'
                      }}
                    >
                      Применить
                    </button>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>
                      Дата архива:
                    </label>
                    <input
                      type="date"
                      value={bulkArchivedAt}
                      onChange={(e) => setBulkArchivedAt(e.target.value)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                    />
                    <button
                      onClick={() => applyToSelected('archivedAt', bulkArchivedAt)}
                      disabled={!bulkArchivedAt || selectedCount === 0}
                      style={{
                        marginTop: '5px',
                        padding: '6px 12px',
                        background: bulkArchivedAt && selectedCount > 0 ? '#667eea' : '#e5e7eb',
                        color: bulkArchivedAt && selectedCount > 0 ? 'white' : '#9ca3af',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: bulkArchivedAt && selectedCount > 0 ? 'pointer' : 'not-allowed',
                        width: '100%'
                      }}
                    >
                      Применить
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                  <button
                    onClick={handleSelectAll}
                    style={{
                      padding: '8px 16px',
                      background: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    {selectAll ? '☑ Снять все' : '☐ Выбрать все'}
                  </button>
                  <button
                    onClick={handleDeleteSelected}
                    disabled={selectedCount === 0}
                    style={{
                      padding: '8px 16px',
                      background: selectedCount > 0 ? '#ef4444' : '#e5e7eb',
                      color: selectedCount > 0 ? 'white' : '#9ca3af',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
                      fontWeight: '500'
                    }}
                  >
                    ❌ Удалить выбранные
                  </button>
                </div>
              </div>

              {/* Таблица задач */}
              <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '12px 8px', textAlign: 'center', width: '40px' }}>☑</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', minWidth: '200px' }}>Название задачи</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', minWidth: '150px' }}>Цель</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', minWidth: '120px' }}>Категория</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', minWidth: '120px' }}>Статус</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', minWidth: '130px' }}>Дата архива</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((task) => (
                      <tr key={task.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={task.selected}
                            onChange={() => handleToggleTask(task.id)}
                            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                          />
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <input
                            type="text"
                            value={task.title}
                            onChange={(e) => handleTaskFieldChange(task.id, 'title', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '14px'
                            }}
                          />
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <select
                            value={task.goalId}
                            onChange={(e) => handleTaskFieldChange(task.id, 'goalId', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '14px'
                            }}
                          >
                            <option value="">Без цели</option>
                            {goals.map(g => (
                              <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <select
                            value={task.categoryId}
                            onChange={(e) => handleTaskFieldChange(task.id, 'categoryId', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '14px'
                            }}
                          >
                            {categories.map(c => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <select
                            value={task.status}
                            onChange={(e) => handleTaskFieldChange(task.id, 'status', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '14px'
                            }}
                          >
                            <option value="planned">Запланировано</option>
                            <option value="in_progress_mine">В процессе (мое)</option>
                            <option value="in_progress_waiting">В процессе (жду)</option>
                            <option value="today">Сегодня</option>
                            <option value="done">Готово</option>
                            <option value="archived">Архив</option>
                          </select>
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <input
                            type="date"
                            value={task.archivedAt}
                            onChange={(e) => handleTaskFieldChange(task.id, 'archivedAt', e.target.value)}
                            disabled={task.status !== 'archived'}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '14px',
                              background: task.status !== 'archived' ? '#f3f4f6' : 'white',
                              cursor: task.status !== 'archived' ? 'not-allowed' : 'text'
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Кнопки действий */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowTable(false)}
                  style={{
                    padding: '12px 24px',
                    background: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    color: '#374151'
                  }}
                >
                  ← Назад
                </button>
                <button
                  onClick={handleImportTasks}
                  disabled={tasks.length === 0}
                  style={{
                    padding: '12px 32px',
                    background: tasks.length > 0 ? '#10b981' : '#d1d5db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: tasks.length > 0 ? 'pointer' : 'not-allowed'
                  }}
                >
                  ✅ Импортировать все ({tasks.length} {tasks.length === 1 ? 'задачу' : tasks.length < 5 ? 'задачи' : 'задач'})
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;
